import { create } from "zustand";
import { authenticatedAxios, baseURL } from "../../apis";

const getErrorMessage = (err) => {
  const errorData = err.response?.data;
  if (typeof errorData === "object" && errorData !== null) {
    return Object.entries(errorData)
      .map(
        ([key, value]) =>
          `${key}: ${Array.isArray(value) ? value.join(", ") : value}`
      )
      .join("\n");
  }
  return errorData || err.message || "An unknown error occurred.";
};

const usePairsStore = create((set, get) => ({
  pairs: [],
  exchanges: [],
  isExchangeLoading: false,
  filters: {
    exchange_id: null,
    target_currency: "",
    base_currency: "",
  },
  pagination: {
    count: 0,
    next: null,
    previous: null,
    page: 1,
    pageSize: 50,
  },
  isLoading: false,
  isCoinLoading: false,
  isLoadingMore: false,
  isSubmitting: false,
  error: null,
  // Track search term separately for debouncing
  searchTerm: "",

  // Fetch list of exchanges
  getExchanges: async () => {
    set({ isExchangeLoading: true });
    try {
      const { data } = await authenticatedAxios.get(
        `${baseURL}/api/admin/coins/get-exchanges`
      );
      const active = (Array.isArray(data) ? data : []).filter((e) => e.active);
      set({ exchanges: active });
      return active;
    } catch (err) {
      console.error("Failed to load exchanges:", getErrorMessage(err));
      set({ exchanges: [] });
      return [];
    } finally {
      set({ isExchangeLoading: false });
    }
  },

  // Build clean params (remove empty values)
  buildParams: (filters, page, pageSize) => {
    const params = { page, page_size: pageSize };

    if (filters.exchange_id) {
      params.exchange_id = filters.exchange_id;
    }
    if (filters.target_currency?.trim()) {
      params.target_currency = filters.target_currency.trim();
    }
    if (filters.base_currency?.trim()) {
      params.base_currency = filters.base_currency.trim();
    }

    return params;
  },

  // Fetch pairs list with pagination (initial load or filter change)
  getPairs: async (customFilters = null, paginationOverride = null) => {
    const filters = customFilters !== null ? customFilters : get().filters;
    const currentPage = paginationOverride?.page || 1;
    const pSize = paginationOverride?.page_size || get().pagination.pageSize;

    set({ isCoinLoading: true, error: null });
    try {
      const params = get().buildParams(filters, currentPage, pSize);

      const { data } = await authenticatedAxios.get(
        `${baseURL}/api/admin/coins/`,
        { params }
      );

      const resultsArray = Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data)
        ? data
        : [];

      set({
        pairs: resultsArray,
        filters,
        pagination: {
          count: data.count ?? resultsArray.length,
          next: data.next ?? null,
          previous: data.previous ?? null,
          page: currentPage,
          pageSize: pSize,
        },
      });

      return resultsArray;
    } catch (err) {
      const msg = getErrorMessage(err);
      console.error("Failed to fetch pairs:", msg);
      set({ pairs: [], error: msg });
      return [];
    } finally {
      set({ isCoinLoading: false });
    }
  },

  // Load more pairs (append to existing list)
  loadMorePairs: async () => {
    const { pagination, filters, pairs, isLoadingMore } = get();

    // Prevent duplicate requests
    if (isLoadingMore || !pagination.next) return;

    set({ isLoadingMore: true });
    try {
      const nextPage = pagination.page + 1;
      const params = get().buildParams(filters, nextPage, pagination.pageSize);

      const { data } = await authenticatedAxios.get(
        `${baseURL}/api/admin/coins/`,
        { params }
      );

      const resultsArray = Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data)
        ? data
        : [];

      set({
        pairs: [...pairs, ...resultsArray],
        pagination: {
          count: data.count ?? pairs.length + resultsArray.length,
          next: data.next ?? null,
          previous: data.previous ?? null,
          page: nextPage,
          pageSize: pagination.pageSize,
        },
      });

      return resultsArray;
    } catch (err) {
      const msg = getErrorMessage(err);
      console.error("Failed to load more pairs:", msg);
      set({ error: msg });
      return [];
    } finally {
      set({ isLoadingMore: false });
    }
  },

  // Search coins with debouncing support
  searchCoins: async (searchQuery) => {
    set({ searchTerm: searchQuery });

    // If search is empty, reset to show all coins
    if (!searchQuery || !searchQuery.trim()) {
      const clearedFilters = {
        ...get().filters,
        target_currency: "",
        base_currency: "",
      };
      await get().getPairs(clearedFilters, {
        page: 1,
        page_size: get().pagination.pageSize,
      });
      return;
    }

    // Parse search query to extract target and base currency
    const query = searchQuery.trim().toUpperCase();
    let target_currency = "";
    let base_currency = "";

    // Check if search has "/" separator (e.g., "BTC/USDT")
    if (query.includes("/")) {
      const parts = query.split("/");
      target_currency = parts[0]?.trim() || "";
      base_currency = parts[1]?.trim() || "";
    } else if (query) {
      // Search in target currency only
      target_currency = query;
    }

    const searchFilters = {
      ...get().filters,
      target_currency,
      base_currency,
    };

    // Reset to page 1 and fetch with new search
    await get().getPairs(searchFilters, {
      page: 1,
      page_size: get().pagination.pageSize,
    });
  },

  // Clear search and reset
  clearSearch: async () => {
    set({ searchTerm: "" });
    const clearedFilters = {
      ...get().filters,
      target_currency: "",
      base_currency: "",
    };
    await get().getPairs(clearedFilters, {
      page: 1,
      page_size: get().pagination.pageSize,
    });
  },

  // Toggle recommendation
  toggleRecommend: async (pairId) => {
    set({ isSubmitting: true, error: null });
    try {
      const { data } = await authenticatedAxios.post(
        `${baseURL}/api/admin/coins/${pairId}/toggle-recommend/`
      );
      set((state) => ({
        pairs: state.pairs.map((p) =>
          p.id === pairId ? { ...p, is_recommended: !p.is_recommended } : p
        ),
      }));
      return data;
    } catch (err) {
      const msg = getErrorMessage(err);
      console.error(`Failed to toggle recommend for pair ${pairId}:`, msg);
      set({ error: msg });
      return null;
    } finally {
      set({ isSubmitting: false });
    }
  },

  // Pagination controls
  setPage: (page) => {
    set((state) => ({
      pagination: { ...state.pagination, page },
    }));
  },

  setFilters: (newFilters) => {
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
    }));
  },

  // Reset pairs (useful when filters change)
  resetPairs: () => {
    set({
      pairs: [],
      searchTerm: "",
      pagination: {
        count: 0,
        next: null,
        previous: null,
        page: 1,
        pageSize: 50,
      },
    });
  },
}));

export default usePairsStore;
